import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../Components';
import { COLORS, formatKWh, CustomLegend, CustomStackedBarTooltip, formatEuros, formatCents } from './ChartUtils';

  // Helper function to detect if we have the new format (nested objects with consumption and cost)
const isNewFormat = (data) => {
  if (!data?.consumption_summary) return false;
  
  // Check the first entry to determine format
  const firstDateKey = Object.keys(data.consumption_summary)[0];
  if (!firstDateKey) return false;
  
  const firstDateData = data.consumption_summary[firstDateKey];
  
  // Check if any property (except total_consumption and total_cost) is an object
  return Object.keys(firstDateData).some(key => {
    return (
      key !== 'total_consumption' &&
      key !== 'total_cost' &&
      firstDateData[key] !== null &&
      typeof firstDateData[key] === 'object'
    );
  });
};

// Helper function to check if consumption_details exist
const hasConsumptionDetails = (data) => {
  //console.log("DATA!!", data)
  return data?.metadata.consumption_details && Object.keys(data.metadata.consumption_details).length > 0;
};

// Format currency value
const formatCost = (value) => {
  return `${value.toFixed(2)} c`;
};

const TotalConsumption = ({ data }) => {
  const [activeData, setActiveData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartRef = useRef(null);
  
  const isNewDataFormat = useMemo(() => isNewFormat(data), [data]);
  
  const processedData = useMemo(() => {
    if (!data?.consumption_summary) return [];

    if (isNewDataFormat) {
      // Process new format (nested objects with consumption and cost)
      return Object.entries(data.consumption_summary).map(([date, dateData]) => {
        const processedEntry = { date };
        
        // Process each device
        Object.entries(dateData).forEach(([deviceKey, deviceData]) => {
          if (deviceKey !== 'total_consumption' && deviceKey !== 'total_cost') {
            // Create separate keys for consumption and cost
            processedEntry[`${deviceKey}_consumption`] = deviceData.consumption;
            processedEntry[`${deviceKey}_cost`] = deviceData.cost;
          } else {
            // Keep total values
            processedEntry[deviceKey] = dateData[deviceKey];
          }
        });
        
        return processedEntry;
      });
    } else {
      // Process old format (flat structure)
      // First, collect all unique device keys from all entries
      const allDeviceKeys = new Set();
      Object.values(data.consumption_summary).forEach(entry => {
        Object.keys(entry).forEach(key => {
          if (key !== 'date' && key !== 'total_consumption' && key !== 'total_cost') {
            allDeviceKeys.add(key);
          }
        });
      });

      // Then process each date entry with consistent keys
      return Object.entries(data.consumption_summary).map(([date, values]) => {
        const { total_cost, ...rest } = values;
        
        // Create base object with date and total_cost
        const processedEntry = { 
          date,
          total_cost: total_cost || 0
        };
        
        // Ensure all device keys exist with default value of 0
        Array.from(allDeviceKeys).forEach(key => {
          processedEntry[key] = 0;
        });
        
        // Overwrite with actual values where they exist
        Object.entries(rest).forEach(([key, value]) => {
          if (key !== 'date') {
            processedEntry[key] = Number(value) || 0;
          }
        });
        
        return processedEntry;
      });
    }
  }, [data, isNewDataFormat]);

  // Extract device keys for consumption and cost
  const deviceKeys = useMemo(() => {
    if (!processedData.length) return { consumption: [], cost: [] };
    
    if (isNewDataFormat) {
      const consumptionKeys = [];
      const costKeys = [];
      
      // Extract consumption and cost keys
      Object.keys(processedData[0]).forEach(key => {
        if (key !== 'date' && key !== 'total_consumption' && key !== 'total_cost') {
          if (key.endsWith('_consumption')) {
            consumptionKeys.push(key);
          } else if (key.endsWith('_cost')) {
            costKeys.push(key);
          }
        }
      });
      
      return { consumption: consumptionKeys, cost: costKeys };
    } else {
      // For old format, just filter out date and total_consumption
      const keys = Object.keys(processedData[0]).filter(key => 
        key !== 'date' && key !== 'total_consumption' && key !== 'total_cost'
      );
      
      return { consumption: keys, cost: [] };
    }
  }, [processedData, isNewDataFormat]);

  useEffect(() => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
    
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };
    
    // Initial update after a short delay to ensure chart is rendered
    const timer = setTimeout(updateWidth, 100);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, [processedData]);

  const handleMouseMove = (props) => {
    if (props.activePayload && props.activePayload.length > 0) {
      setActiveData(props.activePayload[0].payload);
    }
  };

  const handleMouseLeave = () => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
  };
  
  // Determine if the cost data has mixed signs (positive and negative values)
  const hasMixedCostSigns = useMemo(() => {
    if (!isNewDataFormat || deviceKeys.cost.length === 0) return false;
    
    let hasPositive = false;
    let hasNegative = false;
    
    for (const entry of processedData) {
      for (const key of deviceKeys.cost) {
        const value = entry[key];
        if (value > 0) hasPositive = true;
        if (value < 0) hasNegative = true;
        
        if (hasPositive && hasNegative) return true;
      }
    }
    
    return false;
  }, [isNewDataFormat, deviceKeys.cost, processedData]);
  
  // For cost chart, don't use stackId when we have mixed signs
  const costStackId = hasMixedCostSigns ? null : "cost";

  // Use old tooltip for consumption chart, but ensure it works with new format
  const ConsumptionTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    
    if (isNewDataFormat) {
      // Get only consumption data and format for CustomStackedBarTooltip
      const consumptionPayload = payload
        .filter(item => item.dataKey.endsWith('_consumption'))
        .map(item => ({
          ...item,
          name: item.dataKey.replace('_consumption', ''),
          dataKey: item.dataKey.replace('_consumption', '')
        }));
      
      if (consumptionPayload.length === 0) return null;
      
      return (
        <CustomStackedBarTooltip
          active={active}
          payload={consumptionPayload}
          label={label}
          formatter={(value) => formatKWh(value)}
        />
      );
    } else {
      // For old format, use the CustomStackedBarTooltip directly
      return (
        <CustomStackedBarTooltip
          active={active}
          payload={payload}
          label={label}
          formatter={(value) => formatKWh(value)}
        />
      );
    }
  };
  
  // Use CustomStackedBarTooltip for cost when we can, or use custom tooltip for mixed sign scenario
  const CostTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    
    // If we have mixed signs and the tooltip doesn't show all values, use custom tooltip
    if (hasMixedCostSigns) {
      // Get the full data object
      const dataPoint = payload[0].payload;
      
      return (
        <div className="custom-tooltip bg-white p-4 border rounded shadow-lg">
          <p className="font-bold">{label}</p>
          <table className="w-full mt-2">
            <tbody>
              {deviceKeys.cost.map((key, index) => {
                const deviceName = key.replace('_cost', '');
                const value = dataPoint[key] || 0;
                
                return (
                  <tr key={deviceName}>
                    <td className="py-1">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 mr-2" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span>{deviceName}</span>
                      </div>
                    </td>
                    <td className="text-right font-semibold">{formatCost(value)}</td>
                  </tr>
                );
              })}
              
              {dataPoint.total_cost !== undefined && (
                <tr className="border-t">
                  <td className="pt-1 font-bold">Total</td>
                  <td className="pt-1 text-right font-bold">{formatCost(dataPoint.total_cost)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    } else {
      // Use original tooltip for stacked case
      const costPayload = payload
        .filter(item => item.dataKey.endsWith('_cost'))
        .map(item => ({
          ...item,
          name: item.dataKey.replace('_cost', ''),
          dataKey: item.dataKey.replace('_cost', '')
        }));
      
      if (costPayload.length === 0) return null;
      
      return (
        <CustomStackedBarTooltip
          active={active}
          payload={costPayload}
          label={label}
          formatter={(value) => formatCost(value)}
        />
      );
    }
  };
  
  if (!processedData.length) return null;

  // Determine chart heights
  const chartHeight = isNewDataFormat ? 300 : 400;
  const containerHeight = isNewDataFormat ? 650 : 400;

  return (
    <div ref={chartRef} className="relative">
      {/* InfoBoxes Section */}
      {isNewDataFormat && hasConsumptionDetails(data) && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(data.metadata.consumption_details).map(([actorName, details]) => {
            // Prepare data points for InfoBox
            const dataPoints = [
              { label: "Kokonaiskulutus", value: formatKWh(details.total_consumption) },
              { label: "Kokonaishinta", value: formatEuros(details.total_cost) },
              { label: "Keskihinta / kWh", value: formatCents(details.avg_cost_per_kwh) },
              { label: "Keskikulutus / päivä", value: formatKWh(details.avg_consumption_per_day) },
              { label: "Keskihinta / päivä", value: formatEuros(details.avg_cost_per_day) }
            ];
            
            return (
              <InfoBox
                key={actorName} 
                title={actorName}
                dataPoints={dataPoints}
              />
            );
          })}
        </div>
      )}
      
      {/* Consumption Chart */}
      <div className="mb-8">
        {isNewDataFormat && (
          <h3 className="text-lg font-semibold mb-2">
            Kulutus
          </h3>
        )}
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ComposedChart 
            data={processedData}
            margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            
            <YAxis 
              label={{ value: 'Consumption (kWh)', angle: -90, position: 'insideLeft' }}
            />
            
            <Tooltip content={<ConsumptionTooltipContent />} />

            {isNewDataFormat ? (
              // New format - consumption bars
              deviceKeys.consumption.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="consumption"
                  fill={COLORS[index % COLORS.length]}
                  name={key.replace('_consumption', '')}
                />
              ))
            ) : (
              // Old format
              deviceKeys.consumption.length > 0 ? (
                deviceKeys.consumption.map((key, index) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="a"
                    fill={COLORS[index % COLORS.length]}
                    name={key}
                  />
                ))
              ) : (
                // Fallback if no device keys are found
                <Bar
                  dataKey="total_consumption"
                  fill={COLORS[0]}
                  name="Total Consumption"
                />
              )
            )}
          </ComposedChart>
        </ResponsiveContainer>
        
        {/* Consumption Legend */}
        {containerWidth > 0 && activeData && (
          <div className="mt-2">
            {isNewDataFormat ? (
              <CustomLegend 
                payload={deviceKeys.consumption.map((key, index) => ({
                  dataKey: key.replace('_consumption', ''),
                  color: COLORS[index % COLORS.length]
                }))}
                colors={COLORS}
                activeData={activeData}
                containerWidth={containerWidth}
                processedData={processedData}
                valueFormatter={(value, key) => {
                  const fullKey = `${key}_consumption`;
                  return formatKWh(activeData[fullKey] || 0);
                }}
                totalValue={activeData.total_consumption}
                // Pass additional props to handle the new data format
                isNewFormat={isNewDataFormat}
                keySuffix="_consumption"
              />
            ) : (
              <CustomLegend 
                payload={deviceKeys.consumption.map((key, index) => ({
                  dataKey: key,
                  color: COLORS[index % COLORS.length]
                }))}
                colors={COLORS}
                activeData={activeData}
                containerWidth={containerWidth}
                processedData={processedData}
                valueFormatter={(value) => formatKWh(value)}
              />
            )}
          </div>
        )}
      </div>
      
      {/* Cost Chart - Only shown for new data format */}
      {isNewDataFormat && deviceKeys.cost.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-2">
            Hinta
          </h3>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <ComposedChart 
              data={processedData}
              margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45}
                textAnchor="end"
                height={60}
              />
              
              <YAxis 
                domain={hasMixedCostSigns ? ['auto', 'auto'] : [0, 'auto']}
                label={{ value: 'Cost (c)', angle: -90, position: 'insideLeft' }}
                padding={{ top: 20, bottom: 20 }}
              />
              
              <Tooltip content={<CostTooltipContent />} />

              {/* Cost bars */}
              {deviceKeys.cost.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId={costStackId}
                  isAnimationActive={true}
                  fill={COLORS[index % COLORS.length]}
                  name={key.replace('_cost', '')}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
          
          {/* Cost Legend */}
          {containerWidth > 0 && activeData && (
            <div className="mt-2">
              <CustomLegend 
                payload={deviceKeys.cost.map((key, index) => ({
                  dataKey: key.replace('_cost', ''),
                  color: COLORS[index % COLORS.length]
                }))}
                colors={COLORS}
                activeData={activeData}
                containerWidth={containerWidth}
                processedData={processedData}
                valueFormatter={(value, key) => {
                  const fullKey = `${key}_cost`;
                  return formatCost(activeData[fullKey] || 0);
                }}
                totalValue={activeData.total_cost}
                // Pass additional props to handle the new data format
                isNewFormat={isNewDataFormat}
                keySuffix="_cost"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TotalConsumption;